import { FeatureEnum } from '@/graphql/generated';
import { NavItem } from './NavItem';
import { MaturityIcon, ResultsIcon, SurveyIcon } from '@/components/icons';
import { AppRoutes } from '@/screens/AppRoutes';
import { ReactNode } from 'react';
import { usePermissionChecker } from '@/hooks/usePermissionChecker';
import { useTranslation } from '@/hooks/useTranslation';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { generatePath } from 'react-router';
import { useListReportsByEnterprise } from '@/hooks/reports/useListReportsByEnterprise';

export const useDataCollectNavItems = () => {
  const { isGranted } = usePermissionChecker();

  const { t } = useTranslation();
  const items: { key: string; element: ReactNode }[] = [];

  const enterpriseId = useProjectContext()?.enterprise?.id;
  const hasLicense = useProjectContext()?.hasLicense;

  const { reports: enterpriseReports } =
    useListReportsByEnterprise(enterpriseId);

  if (isGranted(FeatureEnum.MaturityEvaluationManagement)) {
    items.push({
      key: 'page.maturity_evaluation',
      element: (
        <NavItem
          name={t('nav:page.maturity_evaluation')}
          Icon={MaturityIcon}
          href={AppRoutes.MaturityEvaluations}
        />
      ),
    });
  }
  if (isGranted(FeatureEnum.ConsultationManagement)) {
    items.push({
      key: 'page.surveys',
      element: (
        <NavItem
          name={t('nav:page.surveys')}
          Icon={SurveyIcon}
          href={AppRoutes.Survey}
        />
      ),
    });
  }

  if (isGranted(FeatureEnum.IndicatorsManagement)) {
    // Group reports by standard and find the most recent campaign for each
    const reportsByStandard = enterpriseReports?.reduce(
      (acc, report) => {
        const standard = report.standard;
        if (!acc[standard]) {
          acc[standard] = report;
        } else {
          // Compare campaign dates to find the most recent
          const currentCampaignDate = new Date(report.campaign.toDate);
          const existingCampaignDate = new Date(acc[standard].campaign.toDate);
          if (currentCampaignDate > existingCampaignDate) {
            acc[standard] = report;
          }
        }
        return acc;
      },
      {} as Record<string, (typeof enterpriseReports)[0]>,
    );

    const standardReports = reportsByStandard
      ? Object.values(reportsByStandard).sort((a, b) =>
          a.standard.localeCompare(b.standard),
        )
      : [];

    items.push({
      key: 'page.standards',
      element: (
        <NavItem
          name={t('nav:page.standards')}
          Icon={ResultsIcon}
          href={generatePath(AppRoutes.Reports)}
          expandable={standardReports.length > 0}
        >
          {standardReports.map((report) => (
            <NavItem
              key={report.id}
              name={t(`enum:standard.${report.standard}`)}
              href={generatePath(AppRoutes.ReportHome, {
                reportId: report.id,
              })}
            />
          ))}
        </NavItem>
      ),
    });
  }

  if (isGranted(FeatureEnum.IndicatorsCollect) && hasLicense) {
    items.push({
      key: 'page.indicators_collect',
      element: (
        <NavItem
          name={t('nav:page.indicators_collect')}
          Icon={ResultsIcon}
          href={AppRoutes.IndicatorsCollect}
        />
      ),
    });
  }

  return items;
};
